import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Avatar, Spinner, EmptyState } from '../components/Components';
import { bookingAPI } from '../utils/api';
import toast from 'react-hot-toast';
import './Bookings.css';

export default function MyBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBookings = async () => {
    try {
      const res = await bookingAPI.getAll();
      setUpcoming(res.data.upcoming || []);
      setPast(res.data.past || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBookings(); }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this session?')) return;
    try {
      await bookingAPI.update(id, { status: 'cancelled' });
      toast.success('Session cancelled');
      loadBookings();
    } catch (err) { toast.error('Failed to cancel'); }
  };

  const getRelativeDay = (dateStr) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const d = new Date(dateStr + 'T00:00:00');
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === 2) return 'Day After Tomorrow';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isCoach = user?.role === 'coach';

  if (loading) return <div className="page-container"><Spinner /></div>;

  return (
    <div className="page-container" style={{ maxWidth: 900 }}>
      <h2 className="bookings-title">{isCoach ? 'Booked Sessions' : 'My Sessions'}</h2>

      {upcoming.length === 0 && past.length === 0 ? (
        <EmptyState
          icon="📅"
          title="No sessions yet"
          description={isCoach ? 'Sessions booked with you will appear here.' : 'Find a coach and book your first session!'}
          action={!isCoach && <Button onClick={() => navigate('/search')}>Find a Coach</Button>}
        />
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="bookings-section">
              <h3 className="bookings-section__title">Upcoming ({upcoming.length})</h3>
              <div className="bookings-list">
                {upcoming.map(b => {
                  const name = isCoach ? b.candidateName : b.coachName;
                  const initials = isCoach
                    ? (b.candidateAvatar || b.candidateName?.split(' ').map(n=>n[0]).join(''))
                    : (b.coachAvatar || b.coachName?.split(' ').map(n=>n[0]).join(''));
                  return (
                    <Card key={b._id} className="booking-item">
                      <div className="booking-item__left">
                        <Avatar initials={initials || '?'} size="md" />
                        <div>
                          <div className="booking-item__name">{name}</div>
                          <div className="booking-item__badges">
                            <Badge variant="primary" size="sm">{b.coachExpertise?.[0] || b.topic || 'Session'}</Badge>
                            {b.duration && <Badge size="sm">{b.duration} min</Badge>}
                          </div>
                          {b.notes && <div className="booking-item__notes">Note: {b.notes}</div>}
                          {b.topic && <div className="booking-item__notes">Topic: {b.topic}</div>}
                        </div>
                      </div>
                      <div className="booking-item__right">
                        <span className={`booking-item__day ${getRelativeDay(b.date) === 'Today' ? 'booking-item__day--today' : ''}`}>
                          {getRelativeDay(b.date)}
                        </span>
                        <span className="booking-item__time">{b.time}</span>
                        <span className="booking-item__status booking-item__status--confirmed">✓ {b.status}</span>
                      </div>
                      <div className="booking-item__actions">
                        <Button variant="danger" size="sm" onClick={() => handleCancel(b._id)}>Cancel</Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div className="bookings-section bookings-section--past">
              <h3 className="bookings-section__title bookings-section__title--past">Past Sessions ({past.length})</h3>
              <div className="bookings-list">
                {past.map(b => {
                  const name = isCoach ? b.candidateName : b.coachName;
                  const initials = isCoach
                    ? (b.candidateAvatar || b.candidateName?.split(' ').map(n=>n[0]).join(''))
                    : (b.coachAvatar || b.coachName?.split(' ').map(n=>n[0]).join(''));
                  return (
                    <Card key={b._id} className="booking-item booking-item--past">
                      <div className="booking-item__left">
                        <Avatar initials={initials || '?'} size="md" />
                        <div>
                          <div className="booking-item__name">{name}</div>
                          <Badge size="sm">{b.coachExpertise?.[0] || b.topic || 'Session'}</Badge>
                        </div>
                      </div>
                      <div className="booking-item__right">
                        <span className="booking-item__date">
                          {new Date(b.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <span className="booking-item__time">{b.time}</span>
                        <span className={`booking-item__status booking-item__status--${b.status}`}>{b.status}</span>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
